import { useEffect } from 'react';
import { Modal, Stack, TextInput, Button, Group } from '@mantine/core';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CONTENT_CONSTRAINTS, LISTLIST_TYPE_LABELS } from '@/lib/constants/appContent';
import { listlistFormSchema } from '../schemas/ListlistForm.schema';
import { MediaStateTypeId } from '@lib2/model/media/MediaStateTypeObj';
import type { ListlistTypeId } from '@lib2/model/listlist/ListlistType';
import type { ListlistFormSchemaData } from '../schemas/ListlistForm.schema';

interface CreateListlistDialogProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: ListlistFormSchemaData) => void;
  typeId: ListlistTypeId;
  isPending?: boolean;
}

export function CreateListlistDialog({
  opened,
  onClose,
  onSubmit,
  typeId,
  isPending,
}: CreateListlistDialogProps) {
  const typeLabel = LISTLIST_TYPE_LABELS[typeId];

  const { control, handleSubmit, reset } = useForm<ListlistFormSchemaData>({
    resolver: zodResolver(listlistFormSchema),
    defaultValues: {
      title: '',
      descShort: '',
      mediaStateTypeId: MediaStateTypeId.Active,
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (opened) {
      reset({
        title: '',
        descShort: '',
        mediaStateTypeId: MediaStateTypeId.Active,
      });
    }
  }, [opened, reset]);

  const { field: titleField, fieldState: titleState } = useController({
    control,
    name: 'title',
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Create ${typeLabel}`}
      size="md"
    >
      <form onSubmit={handleFormSubmit}>
        <Stack gap="md">
          <TextInput
            label={`${typeLabel} Title`}
            value={titleField.value}
            onChange={titleField.onChange}
            onBlur={titleField.onBlur}
            error={titleState.error?.message}
            description={`${titleField.value.length}/${CONTENT_CONSTRAINTS.TITLE_MAX}`}
            maxLength={CONTENT_CONSTRAINTS.TITLE_MAX}
            required
            data-autofocus
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isPending}>
              Create {typeLabel}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
